import { Fragment, useEffect, useState, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import {
  tradeCargoCap,
  useChantier,
  useContributeChantier,
  useMyFleet,
  useOpenChantierTier,
  useQuarryConvoy,
  useQuarryPreview,
  type ChantierConvoyDto,
  type ChantierOpenTierDto,
  type ChantierPathKey,
  type ChantierStateResponse,
  type ChantierTierDto,
  type ChantierWayDto,
  type ResourcesDto,
} from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { formatDuration } from '@/lib/time'
import { missionFleetCap } from '@/lib/fleetGroups'
import {
  GoldIcon,
  WoodIcon,
  RumIcon,
  PrestigeIcon,
  LanternIcon,
  CheckIcon,
  HourglassIcon,
} from '@/components/game/GameIcons'
import { GoldButton } from '@/components/game/ui'
import { ConvoyPace } from '@/components/game/map/tripDuration'
import { SeaScene } from '@/components/game/SeaScene'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import { ShipPickerList } from '@/components/game/map/shipPicker/ShipPickerList'
import { CARGO_SORTS } from '@/components/game/map/shipPicker/useShipFilter'

/**
 * Le Grand Chantier. Direction « la coupe » : l'élévation du Phare porte l'écran,
 * le panneau de travail est à droite, et le tableau comparatif des cinq paliers
 * vit replié en dessous.
 *
 * Un palier est une souscription : on y verse quand on veut, et la maçonnerie
 * démarre au versement qui couvre le devis. Les coûts affichés viennent du
 * serveur, jamais d'un calcul local.
 */
export function Chantier() {
  const { data, isLoading, isError, error } = useChantier()
  const { data: fleet } = useMyFleet()
  const open = useOpenChantierTier()
  const contribute = useContributeChantier()

  if (isLoading) {
    return <div className="max-w-5xl mx-auto p-6 text-ocean-100">Chargement du Grand Chantier…</div>
  }

  if (isError || !data) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <p className="text-rum-500">Grand Chantier indisponible : {(error as Error)?.message}</p>
      </div>
    )
  }

  const resources = fleet?.resources ?? null

  function onOpen(path: ChantierPathKey, label: string) {
    open.mutate(path, {
      onSuccess: () => toast.success(`Palier lancé : ${label}`),
      onError: (err) => toast.error(err.message || 'Impossible d\'ouvrir ce palier'),
    })
  }

  function onPour(instalment: { gold: number; wood: number; rhum: number }) {
    contribute.mutate(instalment, {
      onSuccess: (state) =>
        toast.success(
          state.openTier?.quoteCovered
            ? 'Devis couvert : la maçonnerie commence'
            : 'Versement enregistré',
        ),
      onError: (err) => toast.error(err.message || 'Versement impossible'),
    })
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 pb-[300px] space-y-6">
      <Header state={data} />

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        <Elevation state={data} />

        <div className="space-y-4">
          {!data.eligible ? (
            <LockedPanel reason={data.lockReason} />
          ) : data.crowned ? (
            <CrownedPanel />
          ) : data.openTier ? (
            <OpenTierPanel
              openTier={data.openTier}
              resources={resources}
              pending={contribute.isPending}
              onPour={onPour}
            />
          ) : (
            <StagedTierPanel
              tier={data.tiers.find((t) => t.state === 'next') ?? null}
              stoneStock={data.stoneStock}
              convoy={data.convoy}
              resources={resources}
              pending={open.isPending}
              onOpen={onOpen}
            />
          )}

          {/* Le chantier de pierre ne s'affiche à part que quand l'étape 1 du
              panneau étagé ne le porte pas déjà : palier en cours (on prépare le
              suivant), écran verrouillé, ou couronnement. */}
          {data.openTier || !data.eligible ? <StoneYard state={data} /> : null}
          <ResetNotice />
        </div>
      </div>

      <Ledger state={data} />

      <SeaScene scene="map" />
    </div>
  )
}

function Header({ state }: { state: ChantierStateResponse }) {
  const { season } = state
  const urgent = season.daysRemaining <= 7

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl text-sand-300 flex items-center gap-3">
          <LanternIcon className="w-8 h-8 text-sand-300" aria-hidden="true" />
          Le Grand Chantier
        </h1>
        <p className="text-sm text-ocean-200 mt-1">
          Le Grand Phare, érigé palier par palier quand il n&apos;y a plus rien à construire.
        </p>
        <p className="text-xs text-ocean-300 mt-1">
          <strong className="text-sand-300">{state.erectedCount}</strong> / {state.totalTiers} paliers érigés
        </p>
      </div>
      <div className="text-right">
        <p className="text-[11px] text-ocean-300 uppercase tracking-wider">
          Semaine {season.week} / {season.totalWeeks}
        </p>
        <p className={`font-display text-lg tabular-nums ${urgent ? 'text-rum-100' : 'text-sand-200'}`}>
          {season.daysRemaining} {season.daysRemaining > 1 ? 'jours' : 'jour'} avant le reset
        </p>
      </div>
    </div>
  )
}

/**
 * L'élévation : cinq segments empilés, des assises au couronnement. Peint quand
 * le palier est érigé, échafaudé et partiellement rempli quand il est en cours,
 * fantôme pointillé quand il reste à venir.
 */
function Elevation({ state }: { state: ChantierStateResponse }) {
  const openIndex = state.openTier?.tierIndex ?? null
  const fill = state.openTier?.quotePercent ?? 0

  // De haut en bas : couronnement, lanterne, galerie, fût, assises.
  const bands = [
    { index: 5, d: 'M67 8 l8 -6 8 6 v14 h-16 z', extra: 'M60 22 h30 v20 h-30 z' },
    { index: 4, d: 'M58 42 h34 v30 h-34 z' },
    { index: 3, d: 'M52 72 h46 v34 h-46 z' },
    { index: 2, d: 'M46 106 h58 l-6 108 h-46 z' },
    { index: 1, d: 'M30 214 h90 l8 62 h-106 z' },
  ]

  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-4 flex flex-col items-center gap-3">
      <svg
        viewBox="0 0 150 330"
        className="w-full max-w-[190px] h-auto"
        role="img"
        aria-label={`Le Grand Phare : ${state.erectedCount} paliers sur ${state.totalTiers} érigés`}
      >
        <defs>
          <linearGradient id="chantier-stone" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#C9A55C" />
            <stop offset="0.45" stopColor="#F5EDD7" />
            <stop offset="1" stopColor="#A8862F" />
          </linearGradient>
        </defs>

        {bands.map((band) => {
          const erected = band.index <= state.erectedCount
          const building = band.index === openIndex

          if (erected) {
            return (
              <g key={band.index}>
                <path d={band.d} fill="url(#chantier-stone)" />
                {band.extra ? <path d={band.extra} fill="url(#chantier-stone)" /> : null}
              </g>
            )
          }

          if (building) {
            return (
              <g key={band.index}>
                <path d={band.d} fill="rgba(186,102,59,.14)" stroke="#BA663B" strokeWidth="1.5" />
                {band.extra ? (
                  <path d={band.extra} fill="rgba(186,102,59,.14)" stroke="#BA663B" strokeWidth="1.5" />
                ) : null}
                <text x="112" y="90" fill="#BA663B" fontSize="9" fontFamily="ui-monospace, monospace">
                  {fill} %
                </text>
              </g>
            )
          }

          return (
            <g key={band.index}>
              <path
                d={band.d}
                fill="none"
                stroke="#5FBDD0"
                strokeOpacity="0.45"
                strokeDasharray="3 4"
                strokeWidth="1.5"
              />
              {band.extra ? (
                <path
                  d={band.extra}
                  fill="none"
                  stroke="#5FBDD0"
                  strokeOpacity="0.45"
                  strokeDasharray="3 4"
                  strokeWidth="1.5"
                />
              ) : null}
            </g>
          )
        })}

        {/* Le rocher et la mer */}
        <path d="M6 276 h138 l-10 20 h-118 z" fill="#0A3E4F" />
        <path d="M0 296 h150 v34 h-150 z" fill="#093747" />
        <path d="M0 302 q18 -5 36 0 t36 0 t36 0 t42 0" stroke="#1E96B5" strokeOpacity="0.5" fill="none" strokeWidth="1.5" />
        <path d="M0 314 q22 -5 44 0 t44 0 t62 0" stroke="#1E96B5" strokeOpacity="0.3" fill="none" strokeWidth="1.5" />
      </svg>

      <div className="w-full h-px bg-sand-500/15" />
      <p className="text-[11px] text-ocean-300 uppercase tracking-wider">érigé</p>
      <p className="font-display text-lg text-sand-100">
        {state.erectedCount} palier{state.erectedCount > 1 ? 's' : ''} sur {state.totalTiers}
      </p>
    </div>
  )
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 space-y-3">
      {children}
    </div>
  )
}

function LockedPanel({ reason }: { reason: string | null }) {
  return (
    <Panel>
      <h2 className="font-display text-lg text-sand-300">Le Grand Chantier n&apos;est pas encore ouvert</h2>
      <p className="text-sm text-ocean-200">{reason ?? 'Il te reste du chemin avant d\'ériger un phare.'}</p>
      <p className="text-xs text-ocean-300">
        C&apos;est une œuvre de fin de saison : elle s&apos;adresse aux capitaines qui n&apos;ont plus rien à bâtir.
      </p>
    </Panel>
  )
}

function CrownedPanel() {
  return (
    <Panel>
      <h2 className="font-display text-lg text-sand-300 flex items-center gap-2">
        <CheckIcon className="w-5 h-5 text-green-300" aria-hidden="true" />
        Le Grand Phare est allumé
      </h2>
      <p className="text-sm text-ocean-200">
        La lumière porte jusqu&apos;à l&apos;horizon. Les cinq paliers sont debout, et ton titre est gravé pour de bon.
      </p>
      <p className="text-xs text-ocean-300">
        L&apos;ouvrage se rebâtira la saison prochaine ; le titre, l&apos;ornement et le prestige restent.
      </p>
    </Panel>
  )
}

/** Le palier en cours : les jauges du devis, puis les versements ou le compte à rebours. */
function OpenTierPanel({
  openTier,
  resources,
  pending,
  onPour,
}: {
  openTier: ChantierOpenTierDto
  resources: ResourcesDto | null
  pending: boolean
  onPour: (instalment: { gold: number; wood: number; rhum: number }) => void
}) {
  const countdown = useCountdown(openTier.resolvesAt)

  // Ce que le versement déplacerait réellement : le reste dû, borné par la cave.
  // Le serveur reclampe de son côté, ceci n'est qu'un miroir d'affichage.
  const avail = { gold: resources?.gold ?? 0, wood: resources?.wood ?? 0, rhum: resources?.rum ?? 0 }
  const surplus = {
    gold: Math.min(openTier.remaining.gold, avail.gold),
    wood: Math.min(openTier.remaining.wood, avail.wood),
    rhum: Math.min(openTier.remaining.rhum, avail.rhum),
  }
  const nothingToPour = surplus.gold + surplus.wood + surplus.rhum === 0

  return (
    <Panel>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold bg-ocean-900/60 text-sand-100 rounded px-2 py-1 uppercase tracking-wider">
            Palier {openTier.tierIndex}
          </span>
          <h2 className="font-display text-lg text-sand-300">{openTier.tierLabel}</h2>
        </div>
        <p className="text-[11px] text-ocean-300 uppercase tracking-wider">
          devis couvert à {openTier.quotePercent} %
        </p>
      </div>

      <div className="space-y-2">
        <Gauge
          icon={<RumIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />}
          paid={openTier.paid.rhum}
          owed={openTier.remaining.rhum}
          tone="rum"
        />
        {openTier.remaining.gold + openTier.paid.gold > 0 ? (
          <Gauge
            icon={<GoldIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />}
            paid={openTier.paid.gold}
            owed={openTier.remaining.gold}
            tone="gold"
          />
        ) : null}
        {openTier.remaining.wood + openTier.paid.wood > 0 ? (
          <Gauge
            icon={<WoodIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />}
            paid={openTier.paid.wood}
            owed={openTier.remaining.wood}
            tone="wood"
          />
        ) : null}
      </div>

      <div className="pt-2 border-t border-sand-500/12 space-y-2">
        <p className="text-[11px] text-ocean-300 uppercase tracking-wider">voie retenue</p>
        <p className="text-sm text-sand-100">
          {openTier.pathLabel}, maçonnerie {formatDuration(openTier.durationSeconds)}
        </p>

        {openTier.masonryStarted ? (
          <div className="bg-ocean-900/55 rounded-lg p-2 text-center border border-sand-500/10">
            <p className="text-[11px] text-ocean-300 uppercase tracking-wider flex items-center justify-center gap-1">
              <HourglassIcon className="w-3.5 h-3.5" aria-hidden="true" />
              Maçonnerie en cours
            </p>
            <p className="font-display text-base text-sand-100 tabular-nums">{countdown}</p>
          </div>
        ) : nothingToPour ? (
          <p className="text-xs text-ocean-400 italic">
            Tes réserves ne couvrent rien pour l&apos;instant. Le devis attend, il n&apos;expire pas.
          </p>
        ) : (
          <div className="space-y-2">
            {/* Les boutons DISENT ce qu'ils débitent. Les anciens libellés (« verser
                100 k », « verser tout le surplus ») laissaient deviner le montant
                et l'unité, ce qui est intenable pour une dépense irréversible. */}
            <GoldButton
              onClick={() => onPour(surplus)}
              disabled={pending}
              className="w-full text-sm px-3 py-1.5"
            >
              <span className="inline-flex items-center justify-center gap-2 flex-wrap">
                Verser
                <Amount icon={<RumIcon className="w-3.5 h-3.5" aria-hidden="true" />} value={surplus.rhum} />
                {surplus.gold > 0 ? (
                  <Amount icon={<GoldIcon className="w-3.5 h-3.5" aria-hidden="true" />} value={surplus.gold} />
                ) : null}
                {surplus.wood > 0 ? (
                  <Amount icon={<WoodIcon className="w-3.5 h-3.5" aria-hidden="true" />} value={surplus.wood} />
                ) : null}
              </span>
            </GoldButton>

            {/* Le cas « je garde ma ressource rare pour la chasse » : c'est la
                décision que le lot existe pour poser, elle mérite son propre geste. */}
            {surplus.rhum > 0 && surplus.gold + surplus.wood > 0 ? (
              <button
                type="button"
                onClick={() => onPour({ gold: 0, wood: 0, rhum: surplus.rhum })}
                disabled={pending}
                className="w-full text-xs px-3 py-1.5 rounded-lg border border-sand-500/35 text-ocean-100 hover:text-sand-200 hover:border-sand-300/50 transition-colors disabled:opacity-50"
              >
                <span className="inline-flex items-center justify-center gap-1.5">
                  Ne verser que
                  <Amount icon={<RumIcon className="w-3 h-3" aria-hidden="true" />} value={surplus.rhum} />
                  <span className="text-ocean-300">et garder le reste</span>
                </span>
              </button>
            ) : null}
          </div>
        )}
      </div>
    </Panel>
  )
}

function Gauge({
  icon,
  paid,
  owed,
  tone,
}: {
  icon: ReactNode
  paid: number
  owed: number
  tone: 'rum' | 'gold' | 'wood'
}) {
  const total = paid + owed
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 100
  const fill =
    tone === 'rum'
      ? 'bg-gradient-to-r from-rum-400 to-rum-100'
      : tone === 'gold'
        ? 'bg-gradient-to-r from-sand-500 to-sand-100'
        : 'bg-gradient-to-r from-ocean-500 to-ocean-200'

  return (
    <div>
      <div className="flex items-center justify-between text-xs tabular-nums mb-1">
        <span className="inline-flex items-center gap-1 text-sand-100">
          {icon}
          {paid.toLocaleString('fr-FR')}
        </span>
        <span className="text-ocean-200">/ {total.toLocaleString('fr-FR')}</span>
      </div>
      <div className="h-1.5 bg-ocean-900 rounded-full overflow-hidden border border-sand-500/15">
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/**
 * Le palier à ouvrir, en DEUX TEMPS : ramener la pierre, puis choisir la voie.
 *
 * La séquence porte le panneau parce qu'elle est la vraie source de confusion :
 * sans étapes, un capitaine sans pierre voyait deux cartes mortes portant deux
 * fois le même message, et rien ne lui disait qu'il était en train de faire
 * l'étape 1 plutôt que d'être bloqué. Le choix ne se révèle donc que quand il
 * existe vraiment.
 */
function StagedTierPanel({
  tier,
  stoneStock,
  convoy,
  resources,
  pending,
  onOpen,
}: {
  tier: ChantierTierDto | null
  stoneStock: number
  convoy: ChantierConvoyDto | null
  resources: ResourcesDto | null
  pending: boolean
  onOpen: (path: ChantierPathKey, label: string) => void
}) {
  // Aucune voie présélectionnée : le choix est définitif pour la saison, on ne
  // laisse pas un clic distrait le trancher.
  const [way, setWay] = useState<ChantierPathKey | null>(null)

  if (!tier) {
    return (
      <Panel>
        <h2 className="font-display text-lg text-sand-300">Rien à ouvrir</h2>
        <p className="text-sm text-ocean-200">Le palier suivant n&apos;est pas encore accessible.</p>
      </Panel>
    )
  }

  const stoneReady = tier.stoneReady
  const chosen = tier.ways.find((w) => w.path === way) ?? null

  return (
    <Panel>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold bg-ocean-900/60 text-sand-100 rounded px-2 py-1 uppercase tracking-wider">
            Palier {tier.index}
          </span>
          <h2 className="font-display text-lg text-sand-300">{tier.label}</h2>
        </div>
        <span className="text-[11px] text-ocean-300 uppercase tracking-wider">2 étapes</span>
      </div>
      <p className="text-xs text-ocean-300">{tier.description}</p>

      {tier.closedForSeason ? (
        <p className="text-sm text-rum-100">
          Hors délai : la saison se termine avant qu&apos;on puisse mener ce palier à son terme, par quelque voie
          que ce soit.
        </p>
      ) : (
        <div className="flex flex-col gap-4 pt-1">
          {/* ── Étape 1 : la pierre ─────────────────────────────────────── */}
          <Step n={1} state={stoneReady ? 'done' : 'now'} title="Ramener la pierre de taille">
            {stoneReady ? (
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs text-ocean-100">Pierre de taille sur le quai</p>
                <StoneTally held={stoneStock} quota={tier.stoneQuota} done />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <StoneTally held={stoneStock} quota={tier.stoneQuota} />
                  <span className="text-[11px] text-ocean-300">
                    il manque {tier.stoneMissing.toLocaleString('fr-FR')} pierres
                  </span>
                </div>
                <div className="h-1.5 bg-ocean-900 rounded-full overflow-hidden border border-sand-500/15">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-ocean-500 to-ocean-200"
                    style={{ width: `${tier.stonePercent}%` }}
                  />
                </div>
                <QuarryConvoy convoy={convoy} />
              </div>
            )}
          </Step>

          <div className="h-px bg-sand-500/12" />

          {/* ── Étape 2 : la voie ───────────────────────────────────────── */}
          <Step
            n={2}
            state={stoneReady ? 'now' : 'todo'}
            title="Choisir la voie, puis ouvrir le palier"
          >
            {!stoneReady ? (
              <p className="text-xs text-ocean-200">
                Deux voies, une seule à prendre. Elles se révèlent dès que la pierre est sur le quai.
              </p>
            ) : (
              <div className="space-y-3">
                {/* Le rhum est le socle commun : hissé hors des cartes pour qu'il ne
                    se répète pas et que les deux voies ne se ressemblent plus. */}
                <div className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-dashed border-rum-300/45 bg-rum-500/10 px-3 py-2">
                  <span className="text-[11px] text-ocean-100">Les deux voies boivent le même rhum</span>
                  <span className="inline-flex items-center gap-1 text-sm tabular-nums text-sand-100">
                    <RumIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />
                    {tier.rhumCost.toLocaleString('fr-FR')}
                  </span>
                </div>

                <div
                  role="radiogroup"
                  aria-label="Voie du palier"
                  className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-stretch"
                >
                  {tier.ways.map((w, i) => (
                    <Fragment key={w.path}>
                      {i === 1 ? (
                        <div className="flex sm:flex-col items-center justify-center gap-1.5 text-sand-400">
                          <span className="flex-1 sm:w-px sm:h-auto h-px bg-gradient-to-r sm:bg-gradient-to-b from-transparent via-sand-500/45 to-transparent" />
                          <span className="font-display text-sm">ou</span>
                          <span className="flex-1 sm:w-px sm:h-auto h-px bg-gradient-to-r sm:bg-gradient-to-b from-transparent via-sand-500/45 to-transparent" />
                        </div>
                      ) : null}
                      <WayOption
                        way={w}
                        resources={resources}
                        selected={way === w.path}
                        onSelect={() => setWay(w.path)}
                      />
                    </Fragment>
                  ))}
                </div>

                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[11px] text-ocean-300">
                    Définitif pour ce palier, et décide de son titre.
                  </span>
                  <GoldButton
                    onClick={() => (chosen ? onOpen(chosen.path, chosen.label) : undefined)}
                    disabled={pending || !chosen || !chosen.reachable}
                    className="text-sm px-3 py-1.5"
                  >
                    {chosen
                      ? chosen.reachable
                        ? `Ouvrir ${chosen.label.toLowerCase()}`
                        : 'Cette voie est hors délai'
                      : 'Choisis une voie'}
                  </GoldButton>
                </div>
              </div>
            )}
          </Step>
        </div>
      )}
    </Panel>
  )
}

function Step({
  n,
  state,
  title,
  children,
}: {
  n: number
  state: 'done' | 'now' | 'todo'
  title: string
  children: ReactNode
}) {
  const badge =
    state === 'done'
      ? 'bg-green-400/15 border-green-300/50 text-green-300'
      : state === 'now'
        ? 'bg-sand-300 border-sand-300 text-[#06222b] font-bold'
        : 'border-sand-500/40 text-sand-200 opacity-45'

  return (
    <div className="grid grid-cols-[30px_1fr] gap-3">
      <span
        className={`grid place-items-center w-[30px] h-[30px] rounded-full border text-xs tabular-nums ${badge}`}
        aria-hidden="true"
      >
        {state === 'done' ? '✓' : n}
      </span>
      <div className={state === 'todo' ? 'opacity-45 space-y-1.5' : 'space-y-1.5'}>
        <p className="text-sm text-sand-100">{title}</p>
        {children}
      </div>
    </div>
  )
}

function StoneTally({ held, quota, done = false }: { held: number; quota: number; done?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 text-sm tabular-nums ${done ? 'text-green-300' : 'text-sand-100'}`}>
      <StoneIcon className={`w-3.5 h-3.5 ${done ? 'text-green-300' : 'text-ocean-200'}`} />
      {held.toLocaleString('fr-FR')} / {quota.toLocaleString('fr-FR')}
    </span>
  )
}

/** Pierre de taille : pas une ressource du jeu, donc pas d'icône dans GameIcons. */
function StoneIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className} aria-hidden="true">
      <path d="M4 20l4-8h8l4 8z" />
      <path d="M9 12l3-6 3 6" />
    </svg>
  )
}

/** Une voie, cochable. L'exclusivité est structurelle : un radiogroup, un bouton. */
function WayOption({
  way,
  resources,
  selected,
  onSelect,
}: {
  way: ChantierWayDto
  resources: ResourcesDto | null
  selected: boolean
  onSelect: () => void
}) {
  const isGold = way.secondaryResource === 'gold'
  const accent = isGold ? 'border-l-[3px] border-l-sand-400' : 'border-l-[3px] border-l-ocean-400'
  const picked = selected
    ? isGold
      ? 'bg-sand-300/10 border-sand-400 ring-1 ring-sand-300/35'
      : 'bg-ocean-400/10 border-ocean-400 ring-1 ring-ocean-400/40'
    : 'bg-ocean-900/35 border-sand-500/20'

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      disabled={!way.reachable}
      className={`relative text-left rounded-xl border p-3 flex flex-col gap-2 transition-colors ${accent} ${picked} ${
        way.reachable ? 'hover:border-sand-300/50' : 'opacity-50 cursor-not-allowed'
      }`}
    >
      <span
        className={`absolute top-2.5 right-2.5 grid place-items-center w-4 h-4 rounded-full border-[1.5px] ${
          selected ? (isGold ? 'border-sand-300' : 'border-ocean-300') : 'border-sand-500/50'
        }`}
        aria-hidden="true"
      >
        {selected ? (
          <span className={`w-2 h-2 rounded-full ${isGold ? 'bg-sand-300' : 'bg-ocean-300'}`} />
        ) : null}
      </span>

      <h3 className="font-display text-base text-sand-200 pr-6">{way.label}</h3>
      <p className="text-[11px] text-ocean-300 flex-1">{way.description}</p>

      <span className="flex items-baseline gap-1.5">
        {isGold ? (
          <GoldIcon className="w-[18px] h-[18px] text-sand-300" aria-hidden="true" />
        ) : (
          <WoodIcon className="w-[18px] h-[18px] text-ocean-200" aria-hidden="true" />
        )}
        <Cost
          value={isGold ? way.cost.gold : way.cost.wood}
          have={isGold ? resources?.gold : resources?.wood}
        />
      </span>

      {way.reachable ? (
        <span
          className={`self-start text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${
            isGold ? 'border-sand-300/50 text-sand-300' : 'border-ocean-200/45 text-ocean-200'
          }`}
        >
          {formatDuration(way.durationSeconds)} · {isGold ? 'plus rapide' : 'plus lent'}
        </span>
      ) : (
        <span className="self-start text-[10px] uppercase tracking-wider text-rum-100">Hors délai cette saison</span>
      )}
    </button>
  )
}

/** Un montant chiffré avec son icône, pour un libellé de bouton. */
function Amount({ icon, value }: { icon: ReactNode; value: number }) {
  return (
    <span className="inline-flex items-center gap-1 tabular-nums font-semibold">
      {icon}
      {value.toLocaleString('fr-FR')}
    </span>
  )
}

/**
 * Un montant, rouge s'il dépasse la réserve. L'icône est rendue par l'appelant :
 * c'est elle qui distingue les deux voies, donc elle mérite sa propre taille.
 */
function Cost({ value, have }: { value: number; have: number | undefined }) {
  const enough = have === undefined || have >= value
  return (
    <span className={`font-mono text-xl tabular-nums ${enough ? 'text-sand-100' : 'text-red-400'}`}>
      {value.toLocaleString('fr-FR')}
    </span>
  )
}

/** Le chantier de pierre : ce que les convois ont ramené de la carrière. */
function StoneYard({ state }: { state: ChantierStateResponse }) {
  // Le quota d'un palier OUVERT a déjà été prélevé à son ouverture : viser ce
  // palier réclamerait une pierre déjà payée. On prépare donc celui d'après.
  const openIndex = state.openTier?.tierIndex ?? null
  const next =
    state.tiers.find((t) => (null !== openIndex ? t.index === openIndex + 1 : t.state === 'next')) ?? null

  return (
    <Panel>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display text-base text-sand-300">Le chantier de pierre</h2>
        <span className="font-display text-lg text-sand-100 tabular-nums">
          {state.stoneStock.toLocaleString('fr-FR')}
        </span>
      </div>
      {!next ? (
        <p className="text-[11px] text-ocean-300">
          Plus rien à préparer : le couronnement est le dernier palier.
        </p>
      ) : (
        <>
          <div className="h-1.5 bg-ocean-900 rounded-full overflow-hidden border border-sand-500/15">
            <div
              className="h-full rounded-full bg-gradient-to-r from-ocean-500 to-ocean-200"
              style={{ width: `${next.stonePercent}%` }}
            />
          </div>
          <p className="text-[11px] text-ocean-300">
            {next.stoneReady
              ? `De quoi ouvrir ${next.label.toLowerCase()} ensuite.`
              : `Encore ${next.stoneMissing.toLocaleString('fr-FR')} pierres avant ${next.label.toLowerCase()}.`}
          </p>
        </>
      )}
      <p className="text-[11px] text-ocean-300">
        Les pierres de taille viennent de la carrière et ne servent qu&apos;au Phare. Elles ne se stockent pas
        dans l&apos;entrepôt et ne se perdent pas entre deux paliers.
      </p>
      {/* Pas de lanceur avant le portail : un convoi brûlerait des rations pour un
          tas de pierre qu'on ne pourrait employer nulle part. */}
      {state.eligible ? <QuarryConvoy convoy={state.convoy} /> : null}
    </Panel>
  )
}

/**
 * Armer un convoi. Un chaland rapporte exactement sa cale, donc c'est ici que les
 * modules de cale et la Forge se paient enfin en dehors du commerce.
 */
function QuarryConvoy({ convoy: inFlight }: { convoy: ChantierConvoyDto | null }) {
  const { data: fleet } = useMyFleet()
  const convoy = useQuarryConvoy()
  const [selected, setSelected] = useState<number[]>([])
  const countdown = useCountdown(inFlight?.resolvesAt ?? null)

  const docked = (fleet?.ships ?? []).filter((s) => s.status === 'docked')
  const forgeMult = fleet?.cargoForgeMult ?? 1
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)
  const picked = docked.filter((s) => selected.includes(s.id))
  // Ce que chaque coque ramène VRAIMENT : sa cale (module compris) liftée par la
  // tech de cargaison de la Forge. Le serveur applique le multiplicateur coque par
  // coque et non sur la somme, précisément pour que les lignes ci-dessous
  // s'additionnent au total sans écart à expliquer.
  const loadOf = (cargoCapacity: number) => tradeCargoCap(cargoCapacity, forgeMult)
  const stone = picked.reduce((acc, s) => acc + loadOf(s.cargoCapacity), 0)

  // La durée et les rations viennent du serveur : elles dépendent de la coque la
  // plus lente, de son gréement et des voiles de la Forge, et un miroir client
  // incomplet mentirait. L'aperçu lit le code même du lancement.
  const preview = useQuarryPreview()
  const previewFor = preview.data
  useEffect(() => {
    if (selected.length > 0) preview.mutate(selected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.join(',')])

  // Seul des onze pickers à ne pas passer par la garde partagée jusqu'ici : une
  // épave ou une coque au branle-bas restait cochable, et le cap du Chantier
  // naval (`ChantierException::fleetTooLarge`, api/src/Service/QuarryConvoyService.php:127-129)
  // n'était appliqué que côté serveur.
  function toggle(id: number) {
    setSelected((prev) =>
      toggleShipSelection(prev, id, docked, fleetCap, () => toast.error(`Convoi au max (${fleetCap} navires).`)),
    )
  }

  function selectMany(ids: number[]) {
    setSelected((prev) =>
      selectManyShips(prev, ids, docked, fleetCap, () => toast.error(`Convoi au max (${fleetCap} navires).`)),
    )
  }

  function send() {
    convoy.mutate(selected, {
      onSuccess: (res) => {
        setSelected([])
        toast.success(`Convoi parti : ${res.stone.toLocaleString('fr-FR')} pierres attendues`)
      },
      onError: (err) => toast.error(err.message || 'Convoi impossible'),
    })
  }

  // Un convoi à la fois : tant qu'il est en mer, on montre son retour au lieu de
  // réafficher un lanceur que le serveur refusera.
  if (inFlight) {
    return (
      <div className="pt-3 border-t border-sand-500/12 space-y-1.5">
        <p className="text-[11px] text-ocean-300 uppercase tracking-wider">Convoi en mer</p>
        <div className="bg-ocean-900/55 rounded-lg p-2 text-center border border-sand-500/10">
          <p className="font-display text-base text-sand-100 tabular-nums">{countdown}</p>
          <p className="text-[11px] text-ocean-300">
            {inFlight.stone.toLocaleString('fr-FR')} pierres attendues au déchargement
          </p>
        </div>
      </div>
    )
  }

  if (docked.length === 0) {
    return (
      <p className="text-[11px] text-ocean-400 italic pt-2 border-t border-sand-500/12">
        Aucun navire à quai pour armer un convoi.
      </p>
    )
  }

  return (
    <div className="pt-3 border-t border-sand-500/12 space-y-2">
      <p className="text-[11px] text-ocean-300 uppercase tracking-wider">Armer un convoi</p>

      {/* Colonne de droite = pierres ramenées (la cale liftée par la Forge, via
          `loadOf`), pas les dés ni la cale brute des autres pickers. */}
      <ShipPickerList
        ships={docked}
        selectedIds={selected}
        onToggle={toggle}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelected([])}
        meta={(s) => (
          <span className="flex-none text-ocean-300 tabular-nums" title="Pierres ramenées">
            {loadOf(s.cargoCapacity).toLocaleString('fr-FR')}
          </span>
        )}
        sorts={CARGO_SORTS}
        fleetWord="convoi"
      />

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-sand-100 tabular-nums">
          <p>{stone.toLocaleString('fr-FR')} pierres</p>
        </div>
        <GoldButton
          onClick={send}
          disabled={convoy.isPending || picked.length === 0}
          className="text-sm px-3 py-1.5"
        >
          Envoyer à la carrière
        </GoldButton>
      </div>
      {previewFor && picked.length > 0 ? (
        <p className="text-[11px] text-ocean-300 flex items-center gap-1 flex-wrap">
          <HourglassIcon className="w-3 h-3" aria-hidden="true" />
          {formatDuration(previewFor.durationSeconds)} d&apos;aller-retour
          <span className="text-ocean-400">·</span>
          <span className="inline-flex items-center gap-1">
            <RumIcon className="w-3 h-3 text-sand-300" aria-hidden="true" />
            {previewFor.rhumCost.toLocaleString('fr-FR')} de rations
          </span>
        </p>
      ) : null}
      {/* Sous la même garde que la durée : sans aperçu serveur, il n'y a rien à chiffrer. */}
      {previewFor && picked.length > 1 ? (
        <ConvoyPace ships={picked} tripSeconds={previewFor.durationSeconds} />
      ) : null}
      <p className="text-[11px] text-ocean-300">
        Un convoi de chantier ne peut pas être intercepté. Le gréement et les voiles de la Forge raccourcissent
        le trajet, la cale et ses modules décident du tonnage.
      </p>
    </div>
  )
}

function ResetNotice() {
  return (
    <div className="rounded-xl border border-sand-500/15 bg-ocean-900/30 px-4 py-3">
      <p className="text-xs text-ocean-100">
        Au reset de saison, l&apos;ouvrage se rebâtit de zéro. Le titre, l&apos;ornement et les points de prestige,
        eux, restent gravés sur ton compte.
      </p>
    </div>
  )
}

/** Le tableau comparatif des cinq paliers, replié par défaut. */
function Ledger({ state }: { state: ChantierStateResponse }) {
  return (
    <details className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 overflow-hidden">
      <summary className="cursor-pointer px-5 py-3 font-display text-base text-sand-300 select-none">
        Le devis complet, palier par palier
      </summary>
      <div className="px-5 pb-5 overflow-x-auto">
        <table className="w-full text-xs tabular-nums min-w-[560px]">
          <thead>
            <tr className="text-ocean-300 uppercase tracking-wider text-[10px]">
              <th className="text-left py-2">Palier</th>
              <th className="text-left py-2">À prix d&apos;or</th>
              <th className="text-left py-2">À la sueur et au bois</th>
              <th className="text-right py-2">Prestige</th>
            </tr>
          </thead>
          <tbody>
            {state.tiers.map((tier) => {
              const or = tier.ways.find((w) => w.path === 'or')
              const bois = tier.ways.find((w) => w.path === 'bois')

              return (
                <tr key={tier.key} className="border-t border-sand-500/12">
                  <td className="py-2 pr-3">
                    <span className="text-sand-100">
                      {tier.index}. {tier.label}
                    </span>
                    <span className="block text-[10px] text-ocean-300">{stateLabel(tier)}</span>
                  </td>
                  <td className="py-2 pr-3">{or ? <WayCell way={or} /> : null}</td>
                  <td className="py-2 pr-3">{bois ? <WayCell way={bois} /> : null}</td>
                  <td className="py-2 text-right">
                    <span className="inline-flex items-center gap-1 text-sand-100">
                      <PrestigeIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />
                      {tier.prestige}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </details>
  )
}

function WayCell({ way }: { way: ChantierWayDto }) {
  if (!way.reachable) {
    return <span className="text-[11px] text-rum-100">Hors délai cette saison</span>
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-sand-100">
        <RumIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />
        {way.cost.rhum.toLocaleString('fr-FR')}
      </span>
      {way.cost.gold > 0 ? (
        <span className="inline-flex items-center gap-1 text-sand-100">
          <GoldIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />
          {way.cost.gold.toLocaleString('fr-FR')}
        </span>
      ) : null}
      {way.cost.wood > 0 ? (
        <span className="inline-flex items-center gap-1 text-sand-100">
          <WoodIcon className="w-3.5 h-3.5 text-sand-300" aria-hidden="true" />
          {way.cost.wood.toLocaleString('fr-FR')}
        </span>
      ) : null}
      <span className="text-[10px] text-ocean-300">{formatDuration(way.durationSeconds)}</span>
    </span>
  )
}

function stateLabel(tier: ChantierTierDto): string {
  switch (tier.state) {
    case 'erected':
      return tier.erectedPath === 'or' ? 'érigé à prix d\'or' : 'érigé à la sueur et au bois'
    case 'open':
      return 'en cours'
    case 'next':
      return 'ouvrable'
    default:
      return 'à venir'
  }
}
